<template>
  <div
    v-if="shouldShowBanner"
    class="bg-orange-50 border-l-4 border-orange-400 p-4 mb-6 shadow-sm"
  >
    <div class="flex items-start">
      <div class="flex-shrink-0">
        <svg
          class="h-5 w-5 text-orange-400"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fill-rule="evenodd"
            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
            clip-rule="evenodd"
          />
        </svg>
      </div>
      <div class="ml-3 flex-1">
        <h3 class="text-sm font-medium text-orange-800">
          Account Pending Approval
        </h3>
        <div class="mt-2 text-sm text-orange-700">
          <p>{{ statusMessage }}</p>
          <div v-if="studentStatus === 'pending'" class="mt-2">
            <p class="font-medium">What happens next:</p>
            <ul class="list-disc list-inside mt-1 space-y-1">
              <li>Our team is reviewing your application</li>
              <li>You'll receive an email notification once approved</li>
              <li>This usually takes 1-2 business days</li>
            </ul>
          </div>
          <div v-else-if="studentStatus === 'document_issue'" class="mt-2">
            <p class="font-medium">Action Required:</p>
            <ul class="list-disc list-inside mt-1 space-y-1">
              <li>Please resubmit the required documents</li>
              <li>Check your email for specific instructions</li>
              <li>Go to your Profile tab to access resubmission</li>
            </ul>
          </div>
          <div v-else-if="studentStatus === 'resubmitted_under_review'" class="mt-2">
            <p class="font-medium">Resubmitted Documents Under Review:</p>
            <ul class="list-disc list-inside mt-1 space-y-1">
              <li>Your resubmitted documents are being reviewed</li>
              <li>You'll receive an email notification once the review is complete</li>
              <li>This usually takes 1-2 business days</li>
            </ul>
          </div>
          <div v-else-if="studentStatus === 'rejected'" class="mt-2">
            <p class="font-medium">Need Help?</p>
            <p class="mt-1">
              Contact our support team at 
              <a href="mailto:support@elevated.edu" class="font-medium text-orange-800 hover:text-orange-900 underline">
                support@elevated.edu
              </a>
              for more information.
            </p>
          </div>
        </div>
      </div>
      <div v-if="dismissible" class="flex-shrink-0 ml-4">
        <button
          @click="dismissBanner"
          class="inline-flex text-orange-400 hover:text-orange-600 focus:outline-none focus:text-orange-600"
        >
          <span class="sr-only">Dismiss</span>
          <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path
              fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"
            />
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useStudentStatus } from '@/composables/useStudentStatus';

interface Props {
  dismissible?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  dismissible: false
});

// Use the student status composable
const { status: studentStatus, statusMessage, canAccessFeatures } = useStudentStatus();

// Local dismissal state
const isDismissed = ref(false);

// Computed property to determine if banner should be shown
const shouldShowBanner = computed(() => {
  // Don't show if no status yet (loading)
  if (!studentStatus.value) return false;
  
  // Don't show if dismissed
  if (isDismissed.value) return false;
  
  // Show banner if student cannot access features (not active)
  return !canAccessFeatures.value;
});

// Dismiss banner function
const dismissBanner = () => {
  if (props.dismissible) {
    isDismissed.value = true;
    // Store dismissal in sessionStorage
    sessionStorage.setItem('student_status_banner_dismissed', 'true');
  }
};

// Check if banner was previously dismissed (only if dismissible)
if (props.dismissible) {
  const wasDismissed = sessionStorage.getItem('student_status_banner_dismissed');
  if (wasDismissed === 'true') {
    isDismissed.value = true;
  }
}
</script>
